/**
 * EditingOptions Service
 * Legacy local migration helpers for editing options (export-only settings)
 */
import type { EditingOptions, TextbookOptions, QuestionExportOptions } from '@/types/editingOptions';
import {
  DEFAULT_EDITING_OPTIONS,
  DEFAULT_TEXTBOOK_OPTIONS,
  DEFAULT_QUESTION_EXPORT_OPTIONS,
  EDITING_OPTIONS_STORAGE_KEY,
  TEXTBOOK_FONT_LIMITS,
} from '@/types/editingOptions';

interface StoredEditingOptions {
  options: EditingOptions;
  updatedAt: number;
}

/**
 * Validate and merge textbook options with defaults
 */
function validateTextbookOptions(options: Partial<TextbookOptions> | undefined): TextbookOptions {
  if (!options) return { ...DEFAULT_TEXTBOOK_OPTIONS };

  const result = { ...DEFAULT_TEXTBOOK_OPTIONS };
  for (const key of Object.keys(result) as (keyof TextbookOptions)[]) {
    const fallback = DEFAULT_TEXTBOOK_OPTIONS[key];
    const value = options[key];
    let normalized: string | number | boolean = fallback;
    if (typeof fallback === 'boolean' && typeof value === 'boolean') normalized = value;
    if (typeof fallback === 'number' && typeof value === 'number' && Number.isFinite(value)) {
      const spacing = key.endsWith('LineSpacing');
      normalized = spacing
        ? Math.min(TEXTBOOK_FONT_LIMITS.LINE_SPACING_MAX, Math.max(TEXTBOOK_FONT_LIMITS.LINE_SPACING_MIN, value))
        : Math.min(TEXTBOOK_FONT_LIMITS.SIZE_MAX, Math.max(TEXTBOOK_FONT_LIMITS.SIZE_MIN, value));
    }
    if (typeof fallback === 'string' && typeof value === 'string') {
      if (key.endsWith('Color')) normalized = /^#[0-9a-f]{6}$/i.test(value) ? value : fallback;
      else normalized = value.trim().slice(0, 100) || fallback;
    }
    Object.assign(result, { [key]: normalized });
  }
  return result;
}

/**
 * Validate and merge question export options with defaults
 */
function validateQuestionExportOptions(options: Partial<QuestionExportOptions> | undefined): QuestionExportOptions {
  if (!options) return { ...DEFAULT_QUESTION_EXPORT_OPTIONS };

  return {
    naeyongilchiQuestionType: options.naeyongilchiQuestionType === 0 || options.naeyongilchiQuestionType === 1
      ? options.naeyongilchiQuestionType
      : DEFAULT_QUESTION_EXPORT_OPTIONS.naeyongilchiQuestionType,
  };
}

/**
 * Validate editing options and merge with defaults
 */
export function validateEditingOptions(options: Partial<EditingOptions> | undefined): EditingOptions {
  if (!options) return { ...DEFAULT_EDITING_OPTIONS };

  return {
    textbook: validateTextbookOptions(options.textbook),
    question: validateQuestionExportOptions(options.question),
  };
}

/**
 * Load editing options from localStorage
 */
export function loadEditingOptions(): EditingOptions {
  try {
    const stored = localStorage.getItem(EDITING_OPTIONS_STORAGE_KEY);
    if (stored) {
      const parsed: StoredEditingOptions = JSON.parse(stored);
      return validateEditingOptions(parsed?.options);
    }
  } catch (err) {
    console.error('[EditingOptionsService] Failed to load options:', err);
  }
  return { ...DEFAULT_EDITING_OPTIONS };
}

/**
 * Load updatedAt timestamp from localStorage
 */
export function loadEditingOptionsUpdatedAt(): number {
  try {
    const stored = localStorage.getItem(EDITING_OPTIONS_STORAGE_KEY);
    if (stored) {
      const parsed: StoredEditingOptions = JSON.parse(stored);
      return parsed?.updatedAt ?? 0;
    }
  } catch {
    return 0;
  }
  return 0;
}

/**
 * Clear editing options from localStorage
 */
export function clearEditingOptions(): void {
  try {
    localStorage.removeItem(EDITING_OPTIONS_STORAGE_KEY);
  } catch (err) {
    console.error('[EditingOptionsService] Failed to clear options:', err);
  }
}
